import React, { ReactElement } from 'react';
import { answerTypeLabelKey, formatTranslation, getTranslate, unsafeTranslationKey } from 'intl';
import { CommonTranslationKeys, InspectionTranslationKeys } from 'intl/keys';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import { resolveComplianceOptionLabel } from 'shared/domain/compliance/complianceOptionLabels';
import {
  ChecklistItemAnswerModel,
  ComplianceChecklistAnswerModel,
  NumericChecklistAnswerModel,
  OptionSelectionChecklistAnswerModel,
} from 'shared/domain/template/inspectionTemplateModel';
import {
  getAnswerCardSx,
  getAnswerTypeChipSx,
} from 'views/template/builder/components/checklistItemNode/answerConfig/answerTypeTheme';
import {
  answerCardHeaderSx,
  answerDetailLabelSx,
  answerDetailRowSx,
  answerOptionsListSx,
  questionTextEmptySx,
  answersSectionSx,
  numericDetailsGridSx,
  requiredChipSx,
} from './styles';
import { fieldContentSx } from 'shared/styles/typography';
import { isRichTextEmpty } from 'shared/utils/richText';
import { RichTextInlineView } from 'components/common/RichTextEditor/RichTextInlineView';

interface PreviewDetailRowProps {
  labelId: string;
  value: string;
}

function PreviewDetailRow({
  labelId,
  value,
}: PreviewDetailRowProps): ReactElement {
  const intl = useIntl();

  return (
    <Box sx={answerDetailRowSx}>
      <Typography variant='caption' sx={answerDetailLabelSx}>
        {formatTranslation(intl, unsafeTranslationKey(labelId) )}
      </Typography>
      <Typography variant='body2'>{value}</Typography>
    </Box>
  );
}

function QuestionText({ text }: { text: string }): ReactElement {
  const intl = useIntl();
  const trimmed = text.trim();

  if (!trimmed) {
    return (
      <Typography component='p' sx={questionTextEmptySx}>
        {formatTranslation(intl, CommonTranslationKeys.notFilled)}
      </Typography>
    );
  }

  return (
    <Typography component='p' sx={fieldContentSx}>
      {text}
    </Typography>
  );
}

interface AnswerPreviewCardHeaderProps {
  answer: ChecklistItemAnswerModel;
}

function AnswerPreviewCardHeader({
  answer,
}: AnswerPreviewCardHeaderProps): ReactElement {
  const intl = useIntl();

  return (
    <Box sx={answerCardHeaderSx}>
      <Chip
        label={formatTranslation(intl, answerTypeLabelKey(answer.type))}
        size='small'
        sx={getAnswerTypeChipSx(answer.type)}
      />
      {answer.isRequired ? (
        <Chip
          label={formatTranslation(intl, InspectionTranslationKeys.required)}
          size='small'
          sx={requiredChipSx}
        />
      ) : null}
    </Box>
  );
}

function ComplianceAnswerPreview({
  answer,
}: {
  answer: ComplianceChecklistAnswerModel;
}): ReactElement {
  const intl = useIntl();
  const visibleOptions = answer.optionLabels.filter(
    (optionLabel) => optionLabel.option !== ComplianceAnswerOption.unset
  );

  return (
    <>
      <QuestionText text={answer.label} />
      {visibleOptions.length > 0 ? (
        <Box sx={answerDetailRowSx}>
          <Typography variant='caption' sx={answerDetailLabelSx}>
            {formatTranslation(intl, InspectionTranslationKeys.option)}
          </Typography>
          <Box component='ul' sx={answerOptionsListSx}>
            {visibleOptions.map((optionLabel) => (
              <Typography
                key={optionLabel.option}
                component='li'
                variant='body2'
              >
                {resolveComplianceOptionLabel(
                  optionLabel.option,
                  optionLabel.label,
                  getTranslate(intl)
                )}
              </Typography>
            ))}
          </Box>
        </Box>
      ) : null}
    </>
  );
}

function NumericAnswerPreview({
  answer,
}: {
  answer: NumericChecklistAnswerModel;
}): ReactElement {
  const intl = useIntl();

  return (
    <>
      <QuestionText text={answer.label} />
      <Box sx={numericDetailsGridSx}>
        <PreviewDetailRow
          labelId={InspectionTranslationKeys.unit}
          value={
            answer.unit
              ? String(answer.unit)
              : formatTranslation(intl, CommonTranslationKeys.none)
          }
        />
        {answer.step !== undefined ? (
          <PreviewDetailRow labelId={InspectionTranslationKeys.step} value={String(answer.step)} />
        ) : null}
        {answer.normMin !== undefined ? (
          <PreviewDetailRow
            labelId={InspectionTranslationKeys.normMin}
            value={String(answer.normMin)}
          />
        ) : null}
        {answer.normMax !== undefined ? (
          <PreviewDetailRow
            labelId={InspectionTranslationKeys.normMax}
            value={String(answer.normMax)}
          />
        ) : null}
        {answer.rangeMin !== undefined ? (
          <PreviewDetailRow
            labelId={InspectionTranslationKeys.rangeMin}
            value={String(answer.rangeMin)}
          />
        ) : null}
        {answer.rangeMax !== undefined ? (
          <PreviewDetailRow
            labelId={InspectionTranslationKeys.rangeMax}
            value={String(answer.rangeMax)}
          />
        ) : null}
      </Box>
    </>
  );
}

function TextAnswerPreview({
  answer,
}: {
  answer: ChecklistItemAnswerModel;
}): ReactElement {
  return <QuestionText text={answer.label} />;
}

const MULTISELECT_LABEL_KEY = {
  yes: CommonTranslationKeys.yes,
  no: CommonTranslationKeys.no,
} as const;

function OptionSelectionAnswerPreview({
  answer,
}: {
  answer: OptionSelectionChecklistAnswerModel;
}): ReactElement {
  const intl = useIntl();

  return (
    <>
      <QuestionText text={answer.label} />
      <PreviewDetailRow
        labelId={InspectionTranslationKeys.multiselect}
        value={formatTranslation(
          intl,
          MULTISELECT_LABEL_KEY[answer.isMultiselect ? 'yes' : 'no']
        )}
      />
      {answer.options.length > 0 ? (
        <Box sx={answerDetailRowSx}>
          <Typography variant='caption' sx={answerDetailLabelSx}>
            {formatTranslation(intl, InspectionTranslationKeys.option)}
          </Typography>
          <Box component='ul' sx={answerOptionsListSx}>
            {answer.options.map((option) => (
              <Typography key={option._id} component='li' variant='body2'>
                {isRichTextEmpty(option.label) ? (
                  formatTranslation(intl, CommonTranslationKeys.none)
                ) : (
                  <RichTextInlineView content={option.label} />
                )}
              </Typography>
            ))}
          </Box>
        </Box>
      ) : null}
    </>
  );
}

function AnswerPreviewContent({
  answer,
}: {
  answer: ChecklistItemAnswerModel;
}): ReactElement | null {
  switch (answer.type) {
    case AnswerType.Compliance:
      return <ComplianceAnswerPreview answer={answer} />;
    case AnswerType.Numeric:
      return <NumericAnswerPreview answer={answer} />;
    case AnswerType.Text:
      return <TextAnswerPreview answer={answer} />;
    case AnswerType.OptionSelection:
      return <OptionSelectionAnswerPreview answer={answer} />;
    default:
      return null;
  }
}

function AnswerPreviewCard({
  answer,
}: {
  answer: ChecklistItemAnswerModel;
}): ReactElement {
  return (
    <Box sx={getAnswerCardSx(answer.type)}>
      <Stack spacing={1.5}>
        <AnswerPreviewCardHeader answer={answer} />
        <AnswerPreviewContent answer={answer} />
      </Stack>
    </Box>
  );
}

export interface ChecklistItemAnswersPreviewProps {
  answers: ChecklistItemAnswerModel[];
}

function ChecklistItemAnswersPreview({
  answers,
}: ChecklistItemAnswersPreviewProps): ReactElement | null {
  const intl = useIntl();

  if (!answers.length) {
    return null;
  }

  return (
    <Box sx={answersSectionSx}>
      <Typography variant='caption' color='text.secondary'>
        {formatTranslation(intl, InspectionTranslationKeys.itemPreviewFieldLabelAnswers,)}
      </Typography>
      <Stack spacing={2} sx={{ mt: 0.5 }}>
        {answers.map((answer) => (
          <AnswerPreviewCard key={answer._id} answer={answer} />
        ))}
      </Stack>
    </Box>
  );
}

export const MemoChecklistItemAnswersPreview = React.memo(
  ChecklistItemAnswersPreview
);
